import React, {useContext} from 'react';
import {AnalyticsApiResponse} from 'client/Reports/reports.types';
import './pie.scss';
import {getGuessMetrics, getQuestionMetrics} from 'client/Reports/reports.utils';
import {ReportsContext} from 'client/Reports/Reports.context';
import {Icon, Text} from '@albert-io/atomic';

interface Props {
  data: AnalyticsApiResponse;
}

export const AnswerBreakdown = ({data}: Props) => {
  const {variables, dimensions} = useContext(ReportsContext);
  const guessMetrics = getGuessMetrics(data);

  let contents = (
    <Text size='s' className='u-color_slate-500' italic>
      Unanswered
    </Text>
  );

  if (
    (dimensions.includes('students') && variables.questionFilter) ||
    (dimensions.includes('questions') && variables.studentFilter)
  ) {
    if (guessMetrics && guessMetrics.values.count_correct_attempts > 0) {
      contents = (
        <>
          <Icon icon='check' className='u-color_green-600' />
          <Text bold size='s' className='u-color_green-600  u-mar-l_1'>
            Correct
          </Text>
        </>
      );
    } else if (guessMetrics && guessMetrics.values.count_correct_attempts === 0) {
      contents = (
        <>
          <Icon icon='close' className='u-color_red-500' />
          <Text bold size='s' className='u-color_red-500 u-mar-l_1'>
            Incorrect
          </Text>
        </>
      );
    }
    return (
      <div className='u-display_flex u-justify-content_center u-align-items_center'>{contents}</div>
    );
  }

  let totalAttempts = 0;
  let correctAttempts = 0;
  let incorrectAttempts = 0;
  let totalExpectedAttempts = 0;

  if (guessMetrics) {
    totalAttempts = guessMetrics.values.count_attempts;
    totalExpectedAttempts = totalAttempts;
    correctAttempts = guessMetrics.values.count_correct_attempts;
    incorrectAttempts = guessMetrics.values.count_incorrect_attempts;
  }

  let unattemptedQuestions;
  const currentQuestionMetric = getQuestionMetrics(data);
  if (currentQuestionMetric?.values?.count_unattempted) {
    unattemptedQuestions = currentQuestionMetric.values.count_unattempted;
    totalExpectedAttempts += unattemptedQuestions;
  }

  const correctBoundary = (correctAttempts / totalExpectedAttempts) * 100;
  const incorrectBoundary = (incorrectAttempts / totalExpectedAttempts) * 100 + correctBoundary;

  const calculatedGradient = `conic-gradient(#059669 0 ${correctBoundary}%, #DC4538 ${correctBoundary}% ${incorrectBoundary}%, #e5e7eb 0 100%)`;

  return (
    <div className='attempts-graph u-display_flex u-align-items_center u-justify-content_flex-start u-line-height_150pc'>
      <div className='reports__performance-pie-chart u-position_relative'>
        <div
          className='pie'
          style={{
            backgroundImage: calculatedGradient
          }}
        />
        <div className='hole' />
      </div>
      <div className='u-display_flex u-flex-direction_column u-mar-l_2'>
        {correctAttempts > 0 || incorrectAttempts > 0 ? (
          <>
            <span className='u-color_green-700 u-white-space_nowrap'>
              {correctAttempts} Correct
            </span>
            <span className='u-color_red-500 u-white-space_nowrap'>
              {incorrectAttempts} Incorrect
            </span>
            {totalAttempts ? (
              <span className='u-display_flex u-flex-wrap_wrap'>
                <span className='u-color_slate-600 u-mar-r_1'>{totalAttempts} Total</span>

                {unattemptedQuestions === undefined ? null : (
                  <span className='u-color_slate-500'>({unattemptedQuestions} Unanswered)</span>
                )}
              </span>
            ) : null}
          </>
        ) : (
          <span className='u-color_slate-500 u-white-space_nowrap'>No attempts</span>
        )}
      </div>
    </div>
  );
};
